/**
 * Audit Log CSV Export with MCA Compliance
 *
 * Exports audit trails to CSV format with compliance metadata header rows,
 * hash verification status, and regulatory references.
 *
 * @module utils/auditExportCSV
 */

import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { verifyHashChain, sortEntriesByTimestamp } from './hashUtils';
import { loadAuditLog } from './auditStorage';

/**
 * FT-556: Format an ISO timestamp the exact same clean way the on-screen
 * Equipment Change History view (ChangeLogViewer.js's local `formatDate`)
 * already shows it, so the CSV's Timestamp column reads like "27 Oct 2026,
 * 10:19" instead of a raw machine-format ISO string like
 * "2026-10-27T10:19:19.366Z". Mirrors ChangeLogViewer.js's 'Unknown'
 * fallback for a missing/null timestamp so the CSV never shows a blank or
 * "Invalid Date" cell.
 *
 * @param {string} isoString - ISO timestamp string (may be falsy)
 * @returns {string} Formatted date, or 'Unknown' if isoString is falsy
 */
function formatTimestampForExport(isoString) {
  if (!isoString) return 'Unknown';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * FT-556: Render an entry's `changes` object as plain, human-readable text
 * instead of raw CSV-escaped JSON, matching the plain-text style the
 * on-screen Equipment Change History view (ChangeLogViewer.js's Details
 * column) already uses for the same data: one "key: before → after" line
 * per changed field, using the RAW field key (not a prettified label) and
 * the exact same '(not set)' handling for a null/undefined before or after
 * value. In practice this logging path only ever records one changed field
 * per entry, but multiple keys are handled too, joined with '; ' so the
 * value stays a single, readable CSV cell rather than relying on embedded
 * newlines inside a quoted cell (safer across spreadsheet apps).
 *
 * @param {{before?: Object, after?: Object}|null|undefined} changes
 * @returns {string} Plain-text changes summary, or '' if no changes
 */
function formatChangesForExport(changes) {
  if (!changes || !changes.after) return '';
  const formatVal = (v) => (v === null || v === undefined) ? '(not set)' : String(v);
  return Object.keys(changes.after)
    .map(key => {
      const beforeVal = changes.before?.[key];
      const afterVal = changes.after[key];
      return `${key}: ${formatVal(beforeVal)} → ${formatVal(afterVal)}`;
    })
    .join('; ');
}

/**
 * Export audit log to CSV with compliance metadata header rows
 *
 * @param {string} vesselId - Vessel ID
 * @param {string} moduleName - Module name (equipment, drills, incidents, crew)
 * @param {Array} auditEntries - Array of audit log entries (optional, will load if not provided)
 * @param {Object} vessel - Vessel object with officialNumber, name
 * @returns {Promise<void>}
 */
export async function exportAuditLogToCSV(vesselId, moduleName, auditEntries = null, vessel) {
  try {
    // Load audit entries if not provided
    if (!auditEntries) {
      auditEntries = await loadAuditLog(vesselId, moduleName);
    }

    if (!auditEntries || auditEntries.length === 0) {
      alert('No audit entries found to export.');
      return;
    }

    // Sort entries by timestamp
    const sortedEntries = sortEntriesByTimestamp(auditEntries);

    // Verify hash chain
    const verification = await verifyHashChain(sortedEntries);

    // Get retention period info
    const retention = getRetentionPeriod(moduleName);
    const keepUntilDate = new Date();
    keepUntilDate.setFullYear(keepUntilDate.getFullYear() + retention.years);

    // Create metadata header rows (will appear at top of CSV)
    const metadataRows = formatCSVMetadata(
      vessel,
      moduleName,
      verification.valid,
      sortedEntries.length
    );

    // Create data header
    // FT-530: reordered so the human-relevant columns come first (Item, User,
    // Timestamp, Action), then detail/compliance columns (Reason, Authorized
    // By, Changes), with the technical hash-chain columns last.
    const dataHeader = [
      'Item Name',
      'Item ID',
      'User Name',
      'User ID',
      'Timestamp',
      'Action',
      'Reason',
      'Authorized By',
      'Delete Type',
      'Changes',
      'Hash',
      'Previous Hash',
      'Hash Status'
    ];

    // Create data rows
    const dataRows = sortedEntries.map(entry => {
      const hashStatus = entry.hash
        ? (entry.previousHash ? 'Chained' : 'Genesis')
        : 'Legacy (No Hash)';

      const changesStr = formatChangesForExport(entry.changes);

      // FT-525: entries written by useAuditTrail.logChange use a NESTED shape
      // (entry.user.id / entry.user.name / entry.item.id / entry.item.name).
      // This exporter previously only read the FLAT shape (entry.userId /
      // entry.userName / entry.itemId / entry.itemName), which doesn't exist
      // on real entries — leaving User ID, User Name, Item ID and Item Name
      // blank on every row. Tolerate both shapes, same pattern already used
      // by auditExportPDF.js (FT-512) and ChangeLogViewer.js.
      const userId = entry.userId || entry.user?.id || '';
      const userName = entry.userName || entry.user?.name || '';
      const itemId = entry.itemId || entry.item?.id || '';
      const itemName = entry.itemName || entry.item?.name || '';

      // FT-530: Reason / Authorized By / Delete Type were captured by
      // useAuditTrail.logChange and shown on-screen by ChangeLogViewer, but
      // never made it into the CSV export. Mirror ChangeLogViewer's display:
      // reason label, with any free-text notes appended as a quoted aside.
      //
      // entry.reason is NOT always the structured { code, label, notes }
      // object — useAuditTrail.logChange stores whatever `reason` it's
      // handed unchanged, with no normalizing step, and several real
      // equipment-module call sites (EquipmentRecordsTab.js "added"/
      // "restored" entries, EquipmentDetailView.js serial-number-update
      // entries) pass a plain STRING. Handle both shapes so the Reason cell
      // doesn't silently blank out for the plain-string case (same defect
      // class as the FT-525 User/Item fix). The on-screen ChangeLogViewer
      // has this same plain-string gap — tracked separately as FT-537,
      // out of scope here.
      const reasonStr = typeof entry.reason === 'string'
        ? entry.reason
        : entry.reason?.label
          ? (entry.reason.notes
              ? `${entry.reason.label} ("${entry.reason.notes}")`
              : entry.reason.label)
          : '';
      const authorizedByStr = entry.authorizedBy || '';
      const deleteTypeStr = entry.deleteType || '';

      return [
        itemName,
        itemId,
        userName,
        userId,
        formatTimestampForExport(entry.timestamp),
        entry.action || 'Update',
        reasonStr,
        authorizedByStr,
        deleteTypeStr,
        changesStr,
        entry.hash || '',
        entry.previousHash || '',
        hashStatus
      ];
    });

    // Combine metadata + data
    const allRows = [...metadataRows, dataHeader, ...dataRows];

    // Convert to CSV using papaparse
    const csv = Papa.unparse(allRows, {
      quotes: true,
      delimiter: ',',
      newline: '\n'
    });

    // Create blob and download
    // FT-556 fix-first: the Changes column can now contain a non-ASCII
    // arrow character (→), and item/user names can contain non-ASCII
    // characters too (e.g. "Seán"). Without a UTF-8 BOM, Windows Excel
    // opened by double-click falls back to the legacy Windows codepage and
    // garbles these characters (confirmed: a real exported file without
    // the BOM decodes "→" as "â" under that legacy path). Same BOM pattern
    // already used by drillExportCSV.js.
    const BOM = '﻿'; // UTF-8 BOM for Excel
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const fileTimestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    const officialNumber = vessel.officialNumber || vesselId;
    const filename = `audit_log_${moduleName}_${officialNumber}_${fileTimestamp}.csv`;

    saveAs(blob, filename);

  } catch (error) {
    console.error('Error exporting audit log to CSV:', error);
    alert(`Failed to export audit log to CSV: ${error.message}`);
    throw error;
  }
}

/**
 * Get retention period for module
 *
 * @param {string} moduleName - Module name
 * @returns {Object} Retention info { years, regulation }
 */
export function getRetentionPeriod(moduleName) {
  const retentionPeriods = {
    equipment: { years: 5, regulation: 'WBC3 3.5.2' },
    drills: { years: 2, regulation: 'MGN 71' },
    incidents: { years: 5, regulation: 'MSN 1853' },
    crew: { years: 2, regulation: 'MSN 1865' }
  };

  return retentionPeriods[moduleName.toLowerCase()] || { years: 2, regulation: 'General' };
}

/**
 * Format CSV metadata header
 *
 * @param {Object} vessel - Vessel object
 * @param {string} moduleName - Module name
 * @param {boolean} chainValid - Hash chain validity
 * @param {number} entryCount - Number of entries
 * @returns {Array<Array<string>>} Metadata rows
 */
export function formatCSVMetadata(vessel, moduleName, chainValid, entryCount) {
  const retention = getRetentionPeriod(moduleName);
  const keepUntilDate = new Date();
  keepUntilDate.setFullYear(keepUntilDate.getFullYear() + retention.years);

  return [
    ['FleetSkipper Audit Log Export'],
    [''],
    ['Vessel Name:', vessel.name || 'N/A'],
    ['Official Number:', vessel.officialNumber || 'N/A'],
    ['IMO Number:', vessel.imoNumber || 'N/A'],
    ['Module:', moduleName],
    ['Export Date:', new Date().toISOString()],
    ['Hash Chain Status:', chainValid ? 'VALID' : 'BROKEN'],
    ['Retention Period:', `${retention.years} years (${retention.regulation})`],
    ['Keep Until:', keepUntilDate.toISOString().split('T')[0]],
    ['Total Entries:', entryCount.toString()],
    ['']
  ];
}
